/**
 * CreateSkuDialog.jsx — Extracted from Inventory.jsx as part of the iter-120
 * refactor to keep page files small and the Create-SKU form independently
 * testable. The component is intentionally "dumb": it only renders the form
 * UI, owns no API state, and delegates everything to the parent via props.
 *
 * Props:
 *  - open                boolean — controls dialog visibility
 *  - onOpenChange        (boolean) => void — Radix-style toggle
 *  - newSku              form-state object
 *  - setNewSku           setter for form state
 *  - onSubmit            form submit handler (parent calls API + closes)
 *  - onScanBarcode       () => void — opens scanner with target='new'
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Camera } from '@phosphor-icons/react';

export default function CreateSkuDialog({
  open, onOpenChange, newSku, setNewSku, onSubmit, onScanBarcode,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="create-sku-btn" className="bg-brand hover:bg-brand/90">
          <Plus size={18} className="mr-2" /> New SKU
        </Button>
      </DialogTrigger>
      <DialogContent className="border border-border rounded-none">
        <DialogHeader>
          <DialogTitle>Create New SKU</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="overline">Name</Label>
            <Input value={newSku.name} onChange={(e) => setNewSku({ ...newSku, name: e.target.value })} required className="mt-1" />
          </div>
          <div>
            <Label className="overline">Description</Label>
            <Input value={newSku.description} onChange={(e) => setNewSku({ ...newSku, description: e.target.value })} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline flex items-center justify-between">
                <span>SKU Code</span>
                <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">optional</span>
              </Label>
              <Input
                value={newSku.sku_code}
                onChange={(e) => setNewSku({ ...newSku, sku_code: e.target.value })}
                className="mt-1 font-mono"
                placeholder="Auto-generate"
                maxLength={64}
                autoCapitalize="characters"
                data-testid="new-sku-code"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Leave blank to auto-generate (e.g. SKU-AB12CD34)</p>
            </div>
            <div>
              <Label className="overline flex items-center justify-between">
                <span>Barcode</span>
                <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">optional</span>
              </Label>
              <div className="mt-1 flex gap-1">
                <Input
                  value={newSku.barcode}
                  onChange={(e) => setNewSku({ ...newSku, barcode: e.target.value })}
                  className="font-mono flex-1"
                  placeholder="Manufacturer UPC / EAN / GTIN"
                  maxLength={64}
                  data-testid="new-sku-barcode"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={onScanBarcode}
                  data-testid="new-sku-barcode-scan"
                  title="Scan barcode with camera"
                  className="shrink-0 px-3"
                  aria-label="Scan barcode"
                >
                  <Camera size={16} weight="bold" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Scan or type the existing barcode</p>
            </div>
          </div>
          <div>
            <Label className="overline">Inventory Class</Label>
            <Select value={newSku.inventory_class} onValueChange={(v) => setNewSku({ ...newSku, inventory_class: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="finished_good">Finished Good</SelectItem>
                <SelectItem value="raw_material">Raw Material</SelectItem>
                <SelectItem value="wip">Work in Progress</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Sale Price ($)</Label>
              <Input type="number" step="0.01" value={newSku.unit_price} onChange={(e) => setNewSku({ ...newSku, unit_price: parseFloat(e.target.value) })} className="mt-1" data-testid="new-sku-unit-price" />
              <p className="text-[10px] text-muted-foreground mt-1">What you sell it for.</p>
            </div>
            <div>
              <Label className="overline">Landed Cost ($)</Label>
              <Input type="number" step="0.01" value={newSku.landed_cost ?? 0} onChange={(e) => setNewSku({ ...newSku, landed_cost: parseFloat(e.target.value) })} className="mt-1" data-testid="new-sku-landed-cost" />
              <p className="text-[10px] text-muted-foreground mt-1">Auto-updated when containers are finalized.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Weight per Each (kg)</Label>
              <Input type="number" step="0.001" value={newSku.weight_kg} onChange={(e) => setNewSku({ ...newSku, weight_kg: parseFloat(e.target.value) })} className="mt-1" data-testid="new-sku-weight" />
            </div>
            <div>
              <Label className="overline">Volume per Each (m³)</Label>
              <Input
                type="number" step="0.001"
                value={newSku.volume_m3}
                onChange={(e) => setNewSku({ ...newSku, volume_m3: parseFloat(e.target.value) })}
                className="mt-1"
                data-testid="new-sku-volume"
                placeholder={(() => {
                  const L = parseFloat(newSku.length_cm) || 0;
                  const W = parseFloat(newSku.width_cm) || 0;
                  const H = parseFloat(newSku.height_cm) || 0;
                  return L && W && H ? ((L * W * H) / 1_000_000).toFixed(6) : 'auto from L×W×H';
                })()}
              />
              <p className="text-[10px] text-muted-foreground mt-1">Auto-derived from Each dims below if blank.</p>
            </div>
          </div>

          {/* iter149 — Fractional quantity + Unit of Measure */}
          <div className="grid grid-cols-[auto_1fr] gap-4 items-start bg-amber-50/40 border border-amber-200 px-3 py-3">
            <label className="flex items-center gap-2 text-sm pt-6 cursor-pointer" data-testid="new-sku-fractional-label">
              <input
                type="checkbox"
                checked={!!newSku.allow_fractional}
                onChange={(e) => setNewSku({ ...newSku, allow_fractional: e.target.checked })}
                data-testid="new-sku-allow-fractional"
              />
              <span className="font-bold uppercase tracking-widest text-[11px]">Allow fractional qty</span>
            </label>
            <div>
              <Label className="overline">Unit of measure</Label>
              <select
                value={newSku.unit_of_measure || 'each'}
                onChange={(e) => setNewSku({ ...newSku, unit_of_measure: e.target.value })}
                className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                data-testid="new-sku-uom"
              >
                <option value="each">each (discrete unit)</option>
                <option value="kg">kg (kilograms)</option>
                <option value="g">g (grams)</option>
                <option value="L">L (litres)</option>
                <option value="mL">mL (millilitres)</option>
                <option value="m">m (metres)</option>
                <option value="cm">cm (centimetres)</option>
                <option value="m2">m² (square metres)</option>
                <option value="m3">m³ (cubic metres)</option>
                <option value="hour">hour</option>
                <option value="dozen">dozen</option>
                <option value="pack">pack</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                Tick &ldquo;Allow fractional&rdquo; for SKUs sold by weight, volume, or length (e.g. 0.25 kg flour). Default = whole numbers only.
              </p>
            </div>
          </div>

          {/* iter163 — Reorder thresholds (min / max). Both optional; surfaced
              on create so ops doesn't have to revisit Planning Settings just
              to set the trigger level for a new SKU. */}
          <div className="border border-dashed border-brand/40 bg-brand/[0.03] px-3 py-3" data-testid="new-sku-reorder-section">
            <div className="text-[10px] font-bold uppercase tracking-widest text-foreground mb-2">
              Reorder thresholds <span className="font-normal normal-case tracking-normal text-muted-foreground">— optional</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Min (reorder point)</Label>
                <Input
                  type="number" min={0} step={1}
                  value={newSku.reorder_point ?? ''}
                  placeholder="e.g. 10"
                  onChange={(e) => setNewSku({ ...newSku, reorder_point: e.target.value })}
                  className="mt-1 font-mono"
                  data-testid="new-sku-reorder-point"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Trigger a reorder when on-hand ≤ this.</p>
              </div>
              <div>
                <Label className="overline">Max (top-up level)</Label>
                <Input
                  type="number" min={0} step={1}
                  value={newSku.reorder_max ?? ''}
                  placeholder="e.g. 50"
                  onChange={(e) => setNewSku({ ...newSku, reorder_max: e.target.value })}
                  className="mt-1 font-mono"
                  data-testid="new-sku-reorder-max"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Target stock level after reordering.</p>
              </div>
            </div>
          </div>

          {/* iter194 — Preferred zones. When auto-allocate picks a put-away
              location on a transfer inbound receipt, it prefers locations
              whose `zone` matches one of these labels. Falls back to any
              active location if no match — never blocks receiving. */}
          <div className="border border-dashed border-brand/40 bg-brand/[0.03] px-3 py-3" data-testid="new-sku-zones-section">
            <div className="text-[10px] font-bold uppercase tracking-widest text-foreground mb-2">
              Preferred put-away zones <span className="font-normal normal-case tracking-normal text-muted-foreground">— optional</span>
            </div>
            <Input
              value={(newSku.preferred_zones || []).join(', ')}
              placeholder="e.g. CHILLED, HAZMAT"
              onChange={(e) => setNewSku({
                ...newSku,
                preferred_zones: e.target.value.split(',').map((z) => z.trim().toUpperCase()).filter(Boolean),
              })}
              className="mt-1 font-mono uppercase"
              data-testid="new-sku-preferred-zones"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Comma-separated zone codes. Auto-allocate on inbound transfers routes this SKU here first.
            </p>
          </div>

          {/* iter164 — Multi-UOM pack sizes (Receive / Order / Count in
              alternate units). Base UOM (above) stays as the storage unit;
              each row here is a pack size that converts to base. */}
          <div className="border border-dashed border-brand/40 bg-brand/[0.03] px-3 py-3" data-testid="new-sku-uom-conv-section">
            <div className="text-[10px] font-bold uppercase tracking-widest text-foreground mb-2">
              Receive / order in these UOMs <span className="font-normal normal-case tracking-normal text-muted-foreground">— optional pack sizes</span>
            </div>
            <p className="text-[10px] text-muted-foreground mb-2 leading-snug">
              Base unit is <span className="font-mono uppercase">{(newSku.unit_of_measure || 'each')}</span>.
              Add rows like <span className="font-mono">L → 1000</span> (1 L = 1000 mL) or
              <span className="font-mono"> case → 12000</span> (case of 12×1L).
              Set an optional <strong>price</strong> per UOM (e.g. $5/L) — leave blank to auto-derive from base price.
            </p>
            {(newSku.uom_conversions || []).map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 mb-2" data-testid={`new-sku-uom-row-${i}`}>
                <Input
                  value={row.code}
                  onChange={(e) => {
                    const next = [...newSku.uom_conversions];
                    next[i] = { ...next[i], code: e.target.value };
                    setNewSku({ ...newSku, uom_conversions: next });
                  }}
                  placeholder="L"
                  className="font-mono uppercase text-xs"
                  maxLength={24}
                  data-testid={`new-sku-uom-code-${i}`}
                />
                <Input
                  type="number" min={0} step="any"
                  value={row.factor}
                  onChange={(e) => {
                    const next = [...newSku.uom_conversions];
                    next[i] = { ...next[i], factor: e.target.value };
                    setNewSku({ ...newSku, uom_conversions: next });
                  }}
                  placeholder="1000"
                  className="font-mono text-xs"
                  data-testid={`new-sku-uom-factor-${i}`}
                />
                <Input
                  type="number" min={0} step="0.01"
                  value={row.unit_price ?? ''}
                  onChange={(e) => {
                    const next = [...newSku.uom_conversions];
                    next[i] = { ...next[i], unit_price: e.target.value };
                    setNewSku({ ...newSku, uom_conversions: next });
                  }}
                  placeholder="$ / unit"
                  className="font-mono text-xs"
                  data-testid={`new-sku-uom-price-${i}`}
                />
                <Input
                  value={row.label || ''}
                  onChange={(e) => {
                    const next = [...newSku.uom_conversions];
                    next[i] = { ...next[i], label: e.target.value };
                    setNewSku({ ...newSku, uom_conversions: next });
                  }}
                  placeholder="case of 12×1L"
                  className="text-xs"
                  maxLength={120}
                  data-testid={`new-sku-uom-label-${i}`}
                />
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => {
                    const next = (newSku.uom_conversions || []).filter((_, j) => j !== i);
                    setNewSku({ ...newSku, uom_conversions: next });
                  }}
                  data-testid={`new-sku-uom-remove-${i}`}
                  className="shrink-0"
                  aria-label="Remove UOM row"
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => setNewSku({
                ...newSku,
                uom_conversions: [...(newSku.uom_conversions || []), { code: '', factor: '', label: '' }],
              })}
              data-testid="new-sku-uom-add-row"
            >
              <Plus size={14} className="mr-1" /> Add UOM row
            </Button>
          </div>

          {/* Three-tier UOM dims — Each / Inner / Carton. Default-collapsed
              so simple SKUs get a clean form; ops can expand to enter
              tape-measured dims, and the backend auto-derives volume_m3
              for whichever tier has all three dims set. Inputs are kept
              in cm for human-friendliness. */}
          <details className="border border-border rounded-md" open data-testid="new-sku-uom-section">
            <summary className="cursor-pointer px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
              Dimensions & Pack Sizes (Each · Inner · Carton)
            </summary>

            {/* Each-level dims */}
            <div className="px-3 pt-2 pb-3 border-b border-border">
              <div className="text-[10px] font-bold uppercase tracking-widest text-foreground mb-2">Each (single unit)</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="overline text-[10px]">Length (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.length_cm} placeholder="10"
                    onChange={(e) => setNewSku({ ...newSku, length_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-each-length" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Width (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.width_cm} placeholder="5"
                    onChange={(e) => setNewSku({ ...newSku, width_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-each-width" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Height (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.height_cm} placeholder="2"
                    onChange={(e) => setNewSku({ ...newSku, height_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-each-height" />
                </div>
              </div>
            </div>

            {/* Inner UOM */}
            <div className="px-3 pt-2 pb-3 border-b border-border">
              <div className="text-[10px] font-bold uppercase tracking-widest text-foreground mb-2">Inner pack (e.g. 6-pack)</div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="overline text-[10px]">Units / inner</Label>
                  <Input type="number" min={1} step={1} value={newSku.units_per_inner} placeholder="6"
                    onChange={(e) => setNewSku({ ...newSku, units_per_inner: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-units-per-inner" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Length (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.inner_length_cm} placeholder="30"
                    onChange={(e) => setNewSku({ ...newSku, inner_length_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-inner-length" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Width (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.inner_width_cm} placeholder="10"
                    onChange={(e) => setNewSku({ ...newSku, inner_width_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-inner-width" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Height (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.inner_height_cm} placeholder="4"
                    onChange={(e) => setNewSku({ ...newSku, inner_height_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-inner-height" />
                </div>
              </div>
              <div className="mt-2">
                <Label className="overline text-[10px]">Inner weight (kg)</Label>
                <Input type="number" min={0} step="0.001" value={newSku.inner_weight_kg} placeholder="0.5"
                  onChange={(e) => setNewSku({ ...newSku, inner_weight_kg: e.target.value })}
                  className="mt-1 font-mono max-w-[200px]" data-testid="new-sku-inner-weight" />
              </div>
            </div>

            {/* Carton UOM */}
            <div className="px-3 pt-2 pb-3" data-testid="new-sku-carton-info">
              <div className="text-[10px] font-bold uppercase tracking-widest text-foreground mb-2">Carton (case)</div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="overline text-[10px]">Units / carton</Label>
                  <Input type="number" min={1} step={1} value={newSku.case_pack} placeholder="24"
                    onChange={(e) => setNewSku({ ...newSku, case_pack: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-case-pack" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Length (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.case_length_cm} placeholder="60"
                    onChange={(e) => setNewSku({ ...newSku, case_length_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-case-length" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Width (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.case_width_cm} placeholder="40"
                    onChange={(e) => setNewSku({ ...newSku, case_width_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-case-width" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Height (cm)</Label>
                  <Input type="number" min={0} step="0.1" value={newSku.case_height_cm} placeholder="20"
                    onChange={(e) => setNewSku({ ...newSku, case_height_cm: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-case-height" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="overline text-[10px]">Carton weight (kg)</Label>
                  <Input type="number" step="0.01" min={0} placeholder="12.0"
                    value={newSku.case_weight_kg}
                    onChange={(e) => setNewSku({ ...newSku, case_weight_kg: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-case-weight" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Carton volume (m³)</Label>
                  <Input type="number" step="0.001" min={0}
                    placeholder={(() => {
                      const L = parseFloat(newSku.case_length_cm) || 0;
                      const W = parseFloat(newSku.case_width_cm) || 0;
                      const H = parseFloat(newSku.case_height_cm) || 0;
                      return L && W && H ? ((L * W * H) / 1_000_000).toFixed(6) : '0.048';
                    })()}
                    value={newSku.case_volume_m3}
                    onChange={(e) => setNewSku({ ...newSku, case_volume_m3: e.target.value })}
                    className="mt-1 font-mono" data-testid="new-sku-case-volume" />
                </div>
              </div>
              <p className="pt-3 text-[10px] text-muted-foreground">
                Tip: enter L×W×H to auto-derive volume — or paste the supplier's printed cubic spec directly. <strong>Auto-fill dims from cartons</strong> in the toolbar can divide carton weight/volume by Units/carton to populate the Each-level fields too.
              </p>
            </div>
          </details>

          <Button type="submit" className="w-full bg-brand hover:bg-brand/90">Create SKU</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
